import * as React from 'react';
import type { SxProps} from '@mui/material';
import { Avatar, Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { Camera as CameraIcon } from '@phosphor-icons/react/dist/ssr/Camera';

import { useDialog } from '@/hooks/v2/use-dialog';
import type { Account } from '@/types/v2/account';

interface AccountSettingsHeaderProps {
  account: Account;
  isLoading: boolean;
  error: Error | null;
  sx?: SxProps;
}

export function AccountSettingsHeader({
  account,
  isLoading,
  error,
  sx,
}: AccountSettingsHeaderProps): React.JSX.Element {
  const dialog = useDialog();
  return (
    <Stack spacing={4} sx={sx}>
      {isLoading ? (
        <Stack direction="row" spacing={3} sx={{ alignItems: 'center' }} width="500px">
          <Skeleton height={64} variant="rectangular" width={64} />
          <Stack spacing={1}>
            <Skeleton height={30} variant="text" width={325} />
            <Skeleton height={25} variant="text" width={250} />
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={3} sx={{ alignItems: 'flex-start' }}>
          <Stack direction="row" position="relative" spacing={2} sx={{ alignItems: 'center', flex: '1 1 auto' }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <Box
                onClick={dialog.handleOpen}
                sx={{
                  border: '1px dashed var(--mui-palette-divider)',
                  display: 'inline-flex',
                  p: '4px',
                }}
              >
                <Box sx={{ borderRadius: 'inherit', position: 'relative' }}>
                  <Box
                    sx={{
                      alignItems: 'center',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: 'inherit',
                      bottom: 0,
                      color: 'var(--mui-palette-common-white)',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      left: 0,
                      opacity: 0,
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      zIndex: 1,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <CameraIcon fontSize="var(--icon-fontSize-md)" />
                      <Typography color="inherit" variant="subtitle2">
                        Select
                      </Typography>
                    </Stack>
                  </Box>
                  <Avatar sx={{ '--Avatar-size': '100px' }} variant="square">
                    {account.name.split(' ').map((char, index) => (
                      <Typography key={index} variant="h5">
                        {char.charAt(0)}
                      </Typography>
                    ))}
                  </Avatar>
                </Box>
              </Box>
            </Stack>
            <div>
              <Stack spacing={2}>
                <Typography variant="h4">{account?.name}</Typography>
                <Chip label={`ID: ${account.id}`} size="small" />
              </Stack>
              {account?.address ? <Typography color="text.secondary" variant="body1">
                  {account?.address} {account?.city}, {account?.state} {account?.zip_code}
                </Typography> : null}
            </div>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
